import React from 'react';
import {
  BlockList,
  ResourceCard,
  Icon,
  Tag,
  TextCaption,
  TextBody,
} from '@pypestream/design-system';
import { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';
import { useBobcatFlag } from '../../../hooks';
import { tableStore } from '../../../stores/table-store';
import { LibraryCard } from './library-card';

export const MicroagentCategoryDetail: React.FC<{
  items: ResourceLibItem[];
  disabled?: boolean;
  onSelectItem: (item: ResourceLibItem) => void;
}> = ({ items, disabled, onSelectItem }) => {
  // Project Bobcat: dragging a card onto a Table View row (bobcat-resource-
  // library-drag-drop). Flag off — cards are click-to-add only, unchanged.
  const dragDropAccess = useBobcatFlag('resourceLibraryDragDrop');
  const dragDropEnabled = dragDropAccess === 'allowed' && !disabled;

  if (dragDropAccess === 'allowed') {
    return items.length === 0 ? (
      <p className="c-lib-empty">
        No cataloged examples from your solutions for this category yet.
      </p>
    ) : (
      <div className="c-lib-list">
        {items.map((item) => (
          <LibraryCard
            key={item.id}
            item={item}
            kind="microagent"
            disabled={disabled}
            dragEnabled={dragDropEnabled}
            onSelect={onSelectItem}
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <TextBody variant="secondary" textAlign="center" size="small">
        No cataloged examples from your solutions for this category yet.
      </TextBody>
    );
  }

  return (
    <BlockList>
      {items.map((item) => (
        <div
          key={item.id}
          draggable={dragDropEnabled}
          onDragStart={
            dragDropEnabled
              ? (e) => {
                  if (!item.data) {
                    e.preventDefault();
                    return;
                  }
                  e.dataTransfer.effectAllowed = 'copy';
                  e.dataTransfer.setData('text/plain', item.name ?? '');
                  tableStore.setResourceLibDragData({
                    data: item.data,
                    name: item.name ?? 'Resource',
                  });
                }
              : undefined
          }
          onDragEnd={
            dragDropEnabled
              ? () => tableStore.setResourceLibDragData(null)
              : undefined
          }
        >
          <ResourceCard
            interactive
            disabled={disabled}
            onClick={() => onSelectItem(item)}
            label={item.name}
            description={item.description}
          >
            <Icon name="microagent" size="small" slot="label-prefix" />
            {item.nodes ? (
              <>
                <Tag minimal size="small" slot="label-suffix">
                  {item.nodes}
                </Tag>
                <TextCaption variant="secondary" slot="label-suffix">
                  Nodes
                </TextCaption>
              </>
            ) : null}
            <Icon
              name="plus"
              size="small"
              slot="hover-action"
              color="purple"
              variant="filled"
              weight="medium"
            />
          </ResourceCard>
        </div>
      ))}
    </BlockList>
  );
};
